// buildFullRequestAnalysis.js — assembles the "Full analysis" pane's data for
// ONE captured request/response row: request side + response side + issues +
// (when present) check verdicts, all side by side. Presentation only — every
// value here is already extracted somewhere on the row; this file adds no new
// judgment and runs no new validation. Pure function, ESM, never throws.
// Modeled on utils/buildApsParamConformance.js / utils/buildSignOffReport.js.
//
// ── Gotchas this respects (root CLAUDE.md "Known Gotchas") ─────────────────
//  - GAM cust_params must be parsed from the RAW URL — `validData.cust` is
//    never persisted.
//  - Prebid bids live at `validData.tables.bids`, NOT `validData.bids`.
//  - Timestamps are mixed: the proxy sends an ISO string, live events carry
//    Date.now() (a number). `tsOf()` below is a local mirror of the `ts()`
//    helper — every consumer in this codebase (AdLifecycleView.jsx,
//    VideoLifecycleView.jsx, StreamActivityMonitor.jsx, omidLifecycle.js)
//    keeps its own tiny copy rather than importing across files, since
//    InspectraNetworkView.jsx (where the canonical `ts` is exported) is a
//    257KB component file with heavy imports a pure util shouldn't pull in.
//
// ── Check verdicts (deliberately empty today) ───────────────────────────────
// GAM/APS param conformance is computed SESSION-WIDE by AgentPanel.jsx
// (`apsParamConformanceRows` / `paramConformanceRows`, sourced from a Checks
// run over `prepared.results`) and is never attached back onto an individual
// row anywhere in the proxy or the frontend today — there is no
// `row.checkVerdicts` / `validData.conformance` / `validData.paramConformance`
// field to read. `checkVerdicts` below is always `[]`, but its shape is the
// same row shape ApsParamConformanceTable / ParamConformanceTable already
// render, so a future caller that DOES have per-row-filtered results can pass
// them straight through without a new renderer.

function toMs(v) {
  if (v == null) return 0;
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  const n = Date.parse(v);
  return Number.isNaN(n) ? 0 : n;
}

/** Local mirror of InspectraNetworkView.jsx's exported `ts()` — always returns ms. */
function tsOf(row) {
  return (
    toMs(row?.startedAt) ||
    toMs(row?.startedAtMs) ||
    toMs(row?.timing?.start) ||
    toMs(row?.startedAtISO) ||
    toMs(row?.timestamp) ||
    0
  );
}

/** evaluateCheck's status vocabulary, collapsed to the four verdicts the UI runner
 * renders (root CLAUDE.md: fail->error, warn->warning, pass/ok->pass, info->info). */
function normalizeSeverity(raw) {
  const s = String(raw || '').toLowerCase();
  if (s === 'fail' || s === 'error') return 'error';
  if (s === 'warn' || s === 'warning') return 'warning';
  if (s === 'pass' || s === 'ok') return 'pass';
  return 'info';
}

function hostPathOf(url) {
  try {
    const u = new URL(url);
    return { host: u.hostname, path: u.pathname };
  } catch {
    return { host: null, path: null };
  }
}

function parseQueryParams(url) {
  const out = {};
  try {
    const u = new URL(url);
    u.searchParams.forEach((v, k) => { out[k] = v; });
  } catch { /* no/invalid URL — empty params */ }
  return out;
}

/** GAM cust_params, parsed from the RAW URL (the gotcha: validData.cust is never persisted). */
function parseCustParams(url) {
  const out = {};
  try {
    const u = new URL(url);
    const raw = u.searchParams.get('cust_params') || '';
    if (!raw) return out;
    let decoded = raw;
    try { decoded = decodeURIComponent(decoded); } catch { /* keep raw */ }
    try { decoded = decodeURIComponent(decoded); } catch { /* single-encoded is fine */ }
    decoded.split('&').forEach((pair) => {
      if (!pair) return;
      const eq = pair.indexOf('=');
      if (eq < 0) return;
      const k = pair.slice(0, eq);
      const v = pair.slice(eq + 1);
      try { out[decodeURIComponent(k)] = decodeURIComponent(v); } catch { out[k] = v; }
    });
  } catch { /* malformed URL — empty cust_params */ }
  return out;
}

/** Same grouping AdDebugModal.jsx / AdInspectView already use: bid params
 * (hb_-prefixed / amzn-prefixed), contextual signals (mobian_-prefixed), and
 * everything else. */
function groupQueryParams(qs) {
  const bid = {};
  const contextual = {};
  const other = {};
  for (const [k, v] of Object.entries(qs || {})) {
    if (/^(hb_|amzn)/i.test(k)) bid[k] = v;
    else if (/^mobian_/i.test(k)) contextual[k] = v;
    else other[k] = v;
  }
  return { bid, contextual, other };
}

function mimeOf(headers) {
  if (!headers || typeof headers !== 'object') return null;
  for (const [k, v] of Object.entries(headers)) {
    if (String(k).toLowerCase() === 'content-type') return String(v).split(';')[0].trim() || null;
  }
  return null;
}

function isPlainObject(v) {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}

function buildRequestSide(row) {
  const url = (row && typeof row.url === 'string') ? row.url : '';
  const { host, path } = hostPathOf(url);
  const queryParams = parseQueryParams(url);
  return {
    method: (row && row.method) || 'GET',
    url,
    host,
    path,
    queryParams,
    groupedQueryParams: groupQueryParams(queryParams),
    custParams: parseCustParams(url),
    headers: isPlainObject(row && row.requestHeaders) ? row.requestHeaders : null,
  };
}

function buildResponseSide(row) {
  const vd = isPlainObject(row && row.validData) ? row.validData : {};
  // NOT validData.bids — the tables.bids gotcha.
  const prebidBids = Array.isArray(vd?.tables?.bids) ? vd.tables.bids : [];
  const vastResponse = isPlainObject(vd.vastResponse) ? vd.vastResponse : null;
  const vastSummary = vastResponse
    ? {
        filled: vastResponse.filled ?? null,
        creative: vastResponse.creative ?? null,
        vmap: vastResponse.vmap ?? null,
      }
    : null;

  const body = row && row.responseBody;
  const isBinary = typeof body === 'string' && body.startsWith('__BINARY__');
  const bodyAvailable = !!body && body !== '' && !isBinary;

  return {
    status: (row && (row.status ?? row.responseCode)) ?? null,
    statusText: (row && row.statusText) || null,
    mime: mimeOf(row && row.responseHeaders),
    headers: isPlainObject(row && row.responseHeaders) ? row.responseHeaders : null,
    prebidBids,
    vastSummary,
    // SDK metadata already extracted by the proxy (ATT, EIDs, device, …) — a
    // generic passthrough, not re-derived here.
    sdkSummary: isPlainObject(vd.summary) ? vd.summary : null,
    bodyAvailable,
    bodyNote: bodyAvailable
      ? null
      : row && row.captureSource
        ? `Request-only capture (${row.captureSource}) — the URL came from the device log, not through the proxy, so no response exists.`
        : isBinary
        ? 'Binary response body — not decodable in the browser.'
        : 'Response body not captured for this row.',
  };
}

function buildIssues(row) {
  const list = Array.isArray(row && row.issues) ? row.issues : [];
  return list
    .filter((iss) => iss != null)
    .map((iss) => {
      if (typeof iss === 'string') {
        return { severity: 'info', rawLevel: null, code: null, message: iss };
      }
      if (typeof iss !== 'object') {
        return { severity: 'info', rawLevel: null, code: null, message: String(iss) };
      }
      const rawLevel = iss.level || iss.severity || null;
      return {
        severity: normalizeSeverity(rawLevel),
        rawLevel: rawLevel || null,
        code: iss.code || null,
        message: iss.message || iss.msg || iss.note || null,
      };
    });
}

const EMPTY_META = { requestType: null, requestId: null, device: null, step: null };
const EMPTY_TIMESTAMP = { ms: 0, iso: null };

/**
 * buildFullRequestAnalysis(row) -> the full-analysis pane's data for one row.
 * Tolerates any input — missing fields, wrong types, non-object junk — and
 * never throws; a bad row degrades to an empty-but-well-shaped result rather
 * than crashing the pane.
 */
function buildFullRequestAnalysis(row) {
  try {
    const safeRow = isPlainObject(row) ? row : {};
    const ms = tsOf(safeRow);
    return {
      ok: isPlainObject(row),
      request: buildRequestSide(safeRow),
      response: buildResponseSide(safeRow),
      issues: buildIssues(safeRow),
      // Always [] today — see the header comment for why.
      checkVerdicts: [],
      timestamp: { ms, iso: ms ? new Date(ms).toISOString() : null },
      meta: {
        requestType: safeRow.requestType || safeRow.type || null,
        requestId: safeRow.requestId ?? safeRow.id ?? null,
        device: safeRow.device ?? null,
        step: safeRow.step ?? null,
      },
    };
  } catch {
    return {
      ok: false,
      request: { method: 'GET', url: '', host: null, path: null, queryParams: {}, groupedQueryParams: { bid: {}, contextual: {}, other: {} }, custParams: {}, headers: null },
      response: { status: null, statusText: null, mime: null, headers: null, prebidBids: [], vastSummary: null, sdkSummary: null, bodyAvailable: false, bodyNote: 'Response body not captured for this row.' },
      issues: [],
      checkVerdicts: [],
      timestamp: { ...EMPTY_TIMESTAMP },
      meta: { ...EMPTY_META },
    };
  }
}

export default buildFullRequestAnalysis;
export { buildFullRequestAnalysis };
